import { useState, useEffect, useRef, Dispatch, SetStateAction } from "react";
import axios, { CancelTokenSource } from "axios";
import Pagination from "components/table/pagination";
import SafeArea from "components/safe-area";
import useQueryString from "hooks/useQueryString";
import { useQuery, useMutation } from "react-query";
import { TProperty } from "types/property";
import { Button } from "components/button";
import Field from "components/field";
import { RiAddLine } from "react-icons/ri";
import { useModalContext } from "components/modal";
import NewPropertyModal from "./newPropertyModal";
import PropertyCon from "./propertyCon";
import Spinner from "components/spinner";
import { useTranslation } from 'react-i18next';
import Dropdown from "components/dropdown";
import { FaRegEye, FaRegEyeSlash } from "react-icons/fa";
import { IoClose } from "react-icons/io5";
import { debounce } from "lodash";

interface IFilter {
  id: number;
  label: string;
  value: string;
  input: string;
  active: boolean;
  appointmentTypeId?: string;
  startDate?: string;
  endDate?: string;
};

interface IAppointmentTypeOption {
  id: number;
  label: string;
  active: boolean;
};

const APPOINTMENT_TYPE_DATE_RANGE = "appointment_type_date_range";

const toAppliedFilters = (next: IFilter[]): IFilter[] => {
  return next.filter((f) => {
    if (f.value !== APPOINTMENT_TYPE_DATE_RANGE) {
      return true;
    }
    return Boolean(f.active && f.appointmentTypeId && f.startDate && f.endDate);
  });
};
export interface IPropertySelected {
  id: string;
  customer_id: string;
  address: string;
  city: string;
  state: string;
  zipcode: string;
  zip_ext?: string;
  emails?: string[];
  conflict?: boolean;
};

interface IProps {
  stripped?: boolean;
  selectedProperties?: IPropertySelected[];
  setSelectedProperties?: Dispatch<SetStateAction<IPropertySelected[]>>;
};

const CHUNK_SIZE = 100;

export default function Properties({ stripped, selectedProperties, setSelectedProperties }: IProps) {
  const { t } = useTranslation();
  const tBase = "views.properties.index";
  const tr = (key: string) => t(`${tBase}.${key}`);

  const [search, setSearch] = useState("");
  const [filters, setFilters] = useState<IFilter[]>([]); // used for UI only
  const [appliedFilters, setAppliedFilters] = useState<IFilter[]>([]); // used for API call and debounce
  const [exactMatch, setExactMatch] = useState(false);
  const [selectingAll, setSelectingAll] = useState(false);
  const [selectAllCancelled, setSelectAllCancelled] = useState(false);
  const [cancelTokenSource, setCancelTokenSource] = useState<CancelTokenSource | null>(null);
  const [selectAllProgress, setSelectAllProgress] = useState("");
  const [selectAllResults, setSelectAllResults] = useState<{
    total: number;
    completed: number;
    successful: number;
  }>({
    total: 0,
    completed: 0,
    successful: 0
  });

  const nextFilterId = useRef(1);

  const filterOptions = [
    { label: "City",  value: "city"  },
    { label: "State", value: "state" },
    { label: "Zip",   value: "zip" },
    { label: "Zip Ext", value: "zip_ext" },
    { label: "Tags",  value: "tags"  },
    { label: "Appointment Type + Date Range", value: APPOINTMENT_TYPE_DATE_RANGE },
  ];

  const debouncedApply = useRef(
      debounce((next: IFilter[]) => { setAppliedFilters(toAppliedFilters(next)); }, 500)
     ).current;

  const appointmentTypesQuery = useQuery<IAppointmentTypeOption[]>(
    ["appointment-types"],
    async () => {
      const res = await axios.get(
        `${process.env.REACT_APP_SERVER_URL}/api/v1/company/appointment-types`
      );
      return ((res.data || []) as IAppointmentTypeOption[])
        .filter((opt) => opt.active)
        .sort((a, b) => a.label.localeCompare(b.label));
    }
  );
  const appointmentTypes = appointmentTypesQuery.data || [];

  const queryString = useQueryString();
  const { setModal } = useModalContext();

  const pageIndex =
    queryString.search.page && !Array.isArray(queryString.search.page)
      ? +queryString.search.page
      : 1;

  useEffect(() => {
    queryString.search?.search &&
      !Array.isArray(queryString.search?.search) &&
      setSearch(queryString.search.search);
  }, [queryString.search.search]);

  useEffect(() => {
    if (queryString.search?.exactMatch !== undefined) {
      const urlExactMatch = queryString.search.exactMatch === 'true';
      setExactMatch(urlExactMatch);
    }
  }, [queryString.search.exactMatch]);

  useEffect(() => {
    return () => {
      if (cancelTokenSource) {
        cancelTokenSource.cancel("Component unmounted");
      }
    };
  }, [cancelTokenSource]);

  const query = useQuery<{
    properties: TProperty[] | null;
    page_count: number;
    total_count: number;
  }>(
    ["properties", queryString.search, appliedFilters, exactMatch],
    async () =>
      await axios
        .get(
          `
    ${process.env.REACT_APP_SERVER_URL}/api/v1/company/property`,
          {
            params: {
              ...queryString.search,
              filters: appliedFilters.length > 0 ? JSON.stringify(appliedFilters) : undefined,
              exactMatch: exactMatch.toString(),
            }
          }
        )
        .then((res) => res.data),
    {
      keepPreviousData: true,
    }
  );

  const stableSearchParams = JSON.stringify({
    search: queryString.search.search,
    exactMatch: queryString.search.exactMatch
  });

  // clears selected properties when any of the filtering/searching changes
  useEffect(() => {
    setSelectedProperties && setSelectedProperties([]);
  }, [stableSearchParams, appliedFilters]);

  // Replace the fetchPropertyChunk mutation
  const { mutateAsync: fetchPropertyChunk } = useMutation(
    async ({ page, perPage, cancelToken }: { page: number; perPage: number; cancelToken?: CancelTokenSource }) => {
      const { data } = await axios.get(
        `${process.env.REACT_APP_SERVER_URL}/api/v1/company/property`,
        {
          params: {
            ...queryString.search,
            page,
            perPage,
            filters: appliedFilters.length > 0 ? JSON.stringify(appliedFilters) : undefined,
            exactMatch: exactMatch.toString(),
          },
          cancelToken: cancelToken?.token // Use cancel token
        }
      );
      return data.properties || [];
    }
  );

  const cancelSelectAll = () => {
    setSelectAllCancelled(true);
    
    // Cancel any ongoing HTTP requests
    if (cancelTokenSource) {
      cancelTokenSource.cancel("User cancelled selection");
    }
    
    setSelectingAll(false);
    setSelectAllProgress("Selection cancelled");
    setTimeout(() => {
      setSelectAllProgress("");
      setSelectAllResults({ total: 0, completed: 0, successful: 0 });
      setSelectAllCancelled(false);
      setCancelTokenSource(null);
    }, 2000);
  };

  if (query.isLoading) return <Spinner />;

  const data = query.data;

  console.log(data?.properties);  

  const populateProperties = query.data?.properties?.map((property) => {
    return (
      <PropertyCon 
        key={property.id} 
        property={property} 
        stripped={stripped}
        selectedProperties={selectedProperties}
        setSelectedProperties={setSelectedProperties as any}
      />
    );
  });
  
  const selectAllProperties = async () => {
    if (!setSelectedProperties || !stripped) return;

    const totalCount = data?.total_count || 0;

    // If small dataset, use current method
    if (totalCount <= 50) {
      const all: IPropertySelected[] = data?.properties?.map(p => {
        const billedToEmail = p.billed_to?.email;
        const emails = [...new Set([billedToEmail])];

        return ({
          id: p.id.toString(),
          customer_id: String(p.fk_bcustomer_id),
          address: p.address,
          city: p.city,
          state: p.state,
          zipcode: p.zipcode,
          zip_ext: p.zip_ext,
          emails: emails.filter((email): email is string => !!email),
        })
      }) || [];

      setSelectedProperties((prev) => {
        return [...prev, ...all.filter(p => !prev.some(x => x.id === p.id))];
      });
      return;
    }

    // Chunked loading for large datasets
    const newCancelSource = axios.CancelToken.source();
    setCancelTokenSource(newCancelSource);
    setSelectingAll(true);
    setSelectAllProgress("");
    setSelectAllCancelled(false); // Reset cancel flag
    
    const results = {
      total: totalCount,
      completed: 0,
      successful: 0,
    };
    setSelectAllResults(results);

    try {
      const totalChunks = Math.ceil(totalCount / CHUNK_SIZE);
      let allProperties: any[] = [];
      
      // Process chunks with cancellation checks
      for (let i = 0; i < totalChunks; i++) {
        // Check if cancelled before each chunk
        if (selectAllCancelled) {
          setSelectAllProgress(tr("Selection cancelled"));
          return;
        }

        const page = i + 1;
        setSelectAllProgress(`${tr("Loading")} ${Math.min((i + 1) * CHUNK_SIZE, totalCount)} ${tr("of")} ${totalCount} ${tr("properties")}...`);
        
        try {
          const chunkProperties = await fetchPropertyChunk({ 
            page, 
            perPage: CHUNK_SIZE,
            cancelToken: newCancelSource
          });
          
          // Check if cancelled after chunk loads
          if (selectAllCancelled) {
            setSelectAllProgress(tr("Selection cancelled"));
            return;
          }
          
          const mappedProperties: IPropertySelected[] = chunkProperties.map((p: any) => {
            const billedToEmail = p.billed_to?.email;
            const emails = [...new Set([billedToEmail])];

            return {
              id: p.id.toString(),
              customer_id: String(p.fk_bcustomer_id),
              address: p.address,
              city: p.city,
              state: p.state,
              zipcode: p.zipcode,
              zip_ext: p.zip_ext,
              emails: emails.filter((email): email is string => !!email),
            };
          });
          
          allProperties = [...allProperties, ...mappedProperties];
          results.completed = Math.min((i + 1) * CHUNK_SIZE, totalCount);
          setSelectAllResults({...results});
          
        } catch (chunkError) {
          if (axios.isCancel(chunkError)) {
            setSelectAllProgress(tr("Selection cancelled"));
            return;
          }
          console.error(`Error loading chunk ${page}:`, chunkError);
          // Continue with next chunk instead of failing completely
          continue;
        }
      }
      
      // Final check before applying results
      if (selectAllCancelled) {
        setSelectAllProgress(tr("Selection cancelled"));
        return;
      }
      
      // Filter out already selected and add new ones
      const newProperties = allProperties.filter(p => 
        !selectedProperties?.some(selected => selected.id === p.id)
      );
      
      setSelectedProperties((prev) => [...(prev || []), ...newProperties]);
      setSelectAllProgress(`${tr("Selected")} ${allProperties.length} ${tr("properties")}`);
      results.successful = allProperties.length;
      setSelectAllResults({...results});
      
      // Clear progress after 2 seconds
      setTimeout(() => {
        setSelectAllProgress("");
        setSelectAllResults({ total: 0, completed: 0, successful: 0 });
      }, 2000);
      
    } catch (error) {
      if (axios.isCancel(error)) {
        setSelectAllProgress(tr("Selection cancelled"));
      } else if (!selectAllCancelled) {
        console.error('Error selecting all properties:', error);
        setSelectAllProgress(tr("Error loading properties"));
        setTimeout(() => setSelectAllProgress(""), 3000);
      }
    } finally {
      setSelectingAll(false);
      setCancelTokenSource(null);
    }
  };

  return (
    <div className="divide-y">
      <SafeArea>
        <div className="py-4 space-y-2">
          <div className="flex items-center">
            <h1>{tr("Properties")}</h1>
            <span className="ml-4 text-slate-500 font-regular text-sm">
              {data?.total_count} {tr("Properties")}
            </span>
            {/* Select All properties */}
            <div className="ml-auto flex items-center space-x-2">
              {stripped && setSelectedProperties && (
                <div className="flex flex-col items-end">
                  <div className="flex items-center space-x-2">
                    <button 
                      className={`text-primary ${selectingAll ? 'opacity-50 cursor-not-allowed' : ''}`}
                      onClick={() => {
                        if ((data?.total_count ?? 0) > 1000) {
                          setModal({
                            component: (
                              <div className="!z-[9999]">
                                <div className="text-lg font-semibold mb-4">
                                  <span>{tr("This will select")}</span>
                                  <span className="text-primary mx-1">{data?.total_count || 0} </span>
                                  <span>{tr("properties.")}</span>
                                </div>
                                <div>
                                  {tr("Are you sure?")}
                                </div>
                                <div className="mt-4 flex justify-end space-x-4">
                                  <Button
                                    onClick={() => setModal()}
                                  >
                                    {tr("Cancel")}
                                  </Button>
                                  <Button
                                    onClick={() => {
                                      setModal();
                                      selectAllProperties();
                                    }}
                                    primary
                                  >
                                    {tr("Confirm")}
                                  </Button>
                                </div>
                              </div>
                            ),
                            label: tr("Confirm"),
                          })
                        } else {
                          selectAllProperties();
                        }
                      }}
                      disabled={selectingAll}
                    >
                      {selectingAll ? tr("Selecting...") : tr("Select All")} ({data?.total_count || 0})
                    </button>
                    {selectingAll && (
                      <button
                        className="text-red-500 hover:text-red-700 text-sm underline"
                        onClick={cancelSelectAll}
                      >
                        {tr("Cancel")}
                      </button>
                    )}
                  </div>
                  {(selectingAll || selectAllProgress) && (
                    <div className="text-xs text-blue-600 mr-2 mt-1 whitespace-nowrap max-w-[200px]">
                      {selectAllProgress}
                    </div>
                  )}
                </div>
              )}
              <button 
                className="flex items-center space-x-2 rounded-[4px] px-3 py-[9.4px] bg-[#edf1f4] hover:bg-[#bec1c3]"
                onClick={() => {
                  const newExactMatch = !exactMatch;
                  setExactMatch(newExactMatch);
                  
                  // Reset to page 1 when exactMatch changes and update query string
                  queryString.update({ 
                    search: queryString.search.search, 
                    page: 1,
                    exactMatch: newExactMatch 
                  });
                }}
              >
                <input 
                  type="checkbox"
                  checked={exactMatch}
                />
                <div className="text-[13.6px]">
                  {tr("Exact Match")}
                </div>
              </button>
              <Dropdown
                reference={<Button>+ {tr("Add Filter")}</Button>}
                data={filterOptions.map(opt => ({
                  button: [{
                    label: opt.label,
                    onClick: () => {
                      const newFilter: IFilter = {
                        id: nextFilterId.current++,
                        label: opt.label,
                        value: opt.value,
                        input: "",
                        active: true,
                        appointmentTypeId: "",
                        startDate: "",
                        endDate: "",
                      };
                      setFilters([...filters, newFilter]);
                    },
                  }]
                }))}
              />
              {!stripped && (
                <Button
                  primary
                  onClick={() =>
                    setModal({
                      component: <NewPropertyModal />,
                      label: tr("New Property"),
                    })
                  }
                >
                  <RiAddLine /> {tr("Create Property")}
                </Button>
              )}
            </div>
          </div>
          <Field
            type="input"
            placeholder={tr("Search property")}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onKeyPress={(e) =>
              e.key === "Enter" && queryString.update({ search, page: 1 })
            }
          />
          <div className="flex flex-wrap w-full gap-2">
            {filters.map(f => {
              const chipInputClass = `border border-gray-300 px-1 ${f.active ? "bg-white" : "bg-gray-200"}`;
              const dateInputClass = `w-[8rem] min-w-0 ${chipInputClass} [&::-webkit-datetime-edit]:p-0 [&::-webkit-calendar-picker-indicator]:m-0 [&::-webkit-calendar-picker-indicator]:p-0`;
              return (
                <div key={f.id} className="flex flex-wrap items-center justify-between gap-x-2 gap-y-1 border rounded-md px-2 py-1 bg-slate-200">
                  {f.value === APPOINTMENT_TYPE_DATE_RANGE ? (
                    <>
                      <div>{tr("Appt")}:</div>
                      <select
                        className={`w-[130px] ${chipInputClass}`}
                        value={f.appointmentTypeId || ""}
                        onChange={(e) => {
                          const next = filters.map((x) =>
                            x.id === f.id ? { ...x, appointmentTypeId: e.target.value } : x
                          );
                          setFilters(next);
                          debouncedApply(next);
                        }}
                      >
                        <option value="">{tr("Select type")}</option>
                        {appointmentTypes.map((opt) => (
                          <option key={opt.id} value={String(opt.id)}>
                            {opt.label}
                          </option>
                        ))}
                      </select>
                      <div>{tr("Date")}:</div>
                      <input
                        type="date"
                        className={dateInputClass}
                        value={f.startDate || ""}
                        onChange={(e) => {
                          const next = filters.map((x) =>
                            x.id === f.id ? { ...x, startDate: e.target.value } : x
                          );
                          setFilters(next);
                          debouncedApply(next);
                        }}
                      />
                      <span className="text-slate-500">-</span>
                      <input
                        type="date"
                        className={dateInputClass}
                        value={f.endDate || ""}
                        onChange={(e) => {
                          const next = filters.map((x) =>
                            x.id === f.id ? { ...x, endDate: e.target.value } : x
                          );
                          setFilters(next);
                          debouncedApply(next);
                        }}
                      />
                    </>
                  ) : (
                    <>
                      <div>{tr(f.label)}:</div>
                      <input
                        className={`w-[100px] ${chipInputClass}`}
                        value={f.input}
                        onChange={(e) => {
                          const next = filters.map((x) =>
                            x.id === f.id ? { ...x, input: e.target.value } : x
                          );
                          setFilters(next);
                          debouncedApply(next);
                        }}
                      />
                    </>
                  )}
                  <div className="flex items-center space-x-1">
                    <button
                      className="text-secondary hover:text-secondary-dark"
                      onClick={() => {
                        const next = filters.map((x) =>
                          x.id === f.id ? { ...x, active: !x.active } : x
                        );
                        setFilters(next);
                        debouncedApply(next);
                      }}
                    >
                      {f.active ? (
                        <FaRegEye size={16} />  
                      ) : (
                        <FaRegEyeSlash size={16} />
                      )}
                    </button>
                    <button 
                      className="text-red-500 hover:text-red-600"
                      onClick={() => {
                        const next = filters.filter((x) => x.id !== f.id);
                        setFilters(next);
                        debouncedApply(next); 
                      }}
                    >
                      <IoClose size={20} />
                    </button>
                  </div>
                </div>
              )
            })}
            {filters.length > 0 && (
              <button
                className="text-primary hover:text-primary-dark"
                onClick={() => {
                  setFilters([]);
                  setAppliedFilters([]);
                }}
              >
                {tr("Clear All")}
              </button>
            )}
          </div>
        </div>
      </SafeArea>
      <SafeArea>
        <div className="flex flex-col items-center">
          <div className="divide-y w-full">{populateProperties}</div>
          <Pagination
            pageCount={data?.page_count}
            pageIndex={pageIndex}
            handleButtonClick={(index) =>
              queryString.update({
                search: queryString.search.search,
                page: index,
              })
            }
          />
        </div>
      </SafeArea>
    </div>
  );
}
